// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierBuild.jsx   ·   THE BOARDWALK, BUILT
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-13: "no more floating rings and pictures — let's make
// it look like an actual real deal Pier."
//
// ⚠⚠ THIS SUPERSEDES THE 2026-08-25 RULING "the buildings and everything
// else 2D as well — just the 3D camera." Standing the 2D art up on
// camera-facing cards is exactly what read as floating rings and
// pictures, so the town is architecture now: every wing owns a building
// on the deck's edge and its DOOR is the portal. The players stay 2D
// sprites — the ruling about PEOPLE was not revisited, and terror-assets
// sends no CORS headers, so a sprite can never become a texture anyway.
//
// ── ⭐ THE SWAP POINT SURVIVES ──────────────────────────────────────
// caP'n, 2026-08-25: "eventually I will replace the 2D art with my own,
// so it has to be easy to replace." Every structure here is ONE
// component, and `PIER_PIECES` in Pier3D is the one table that says which
// component stands for which interactable. Replacing W'ily's post is one
// row, never a hunt.
//
// ── THE LAWS ────────────────────────────────────────────────────────
//  · ZERO NEW LIGHTS. Every lamp, window, neon and rift is emissive or
//    basic material. The scene keeps its three lights forever.
//  · A BUILDING IS A HANDFUL OF DRAWS. Parts are merged by material
//    (`Merger`), so a shopfront with sixty pieces is ~ten draw calls.
//  · NO PRNG DURING RENDER. Variety is seeded off the wing's id inside a
//    useMemo, so a shop looks the same on every client, forever.
//  · ONE useFrame FOR EVERY ANIMATED THING. Rifts, neons and the Well's
//    crest register a tick in `BUILD_TICKS`; Pier3D drives them all.
//  · IT FADES WHEN IT HIDES YOU. Buildings register in `OCCLUDERS`;
//    Pier3D's fader thins any that stand between the lens and the player,
//    because a real town has buildings on the camera's side too.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Merger, boxUV, hashId, mulberry32 } from "./pierTex";
import {
  clapTex, shingleTex, timberTex, stoneTex, windowTex, boardsTex, doorTex,
  awningTex, signTex, neonTex, glyphTex, riftTex, poolTex,
} from "./pierPaint";

// ── THE REGISTRIES ──────────────────────────────────────────────────
// Module-level on purpose: React context does not cross the Canvas.
export const BUILD_TICKS = new Set();   // fn(t, dt)
export const OCCLUDERS = new Set();     // { box: Box3, mats: Material[], k }

export const TIMBER_DARK = "#3a2818";
const DOOR_DARK = "#07050c";

// Tick registration with automatic cleanup.
function useTick(fn, deps) {
  useEffect(() => {
    BUILD_TICKS.add(fn);
    return () => { BUILD_TICKS.delete(fn); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Register a structure's MASS (a local box, not its whole object — an
// awning or a pool of light on the planks in front must not count) plus
// its materials, for the camera fader.
export function useOccluder(ref, localBox, mats, deps) {
  const entryRef = useRef(null);
  useEffect(() => {
    const o = ref.current;
    if (!o) return undefined;
    o.updateWorldMatrix(true, false);
    const box = localBox.clone().applyMatrix4(o.matrixWorld);
    // ⚠ ONLY FULL-HEIGHT BUILDINGS CUT AWAY. A stall or a wagon is shorter than
    // the sightline over a player's head, and fading one erased the very
    // landmark you had walked up to — Ally's wagon went, leaving her name
    // board hanging in the air. (Visual QA, 2026-09-14.)
    if (box.max.y - box.min.y < 3.6) return undefined;
    const entry = { box, mats: mats.filter(Boolean), k: 1 };
    OCCLUDERS.add(entry);
    entryRef.current = entry;
    return () => {
      OCCLUDERS.delete(entry);
      entryRef.current = null;
      // ⚠ hand the materials back OPAQUE. A structure re-registering while
      // ghosted starts its new entry at k=1, which the driver reads as
      // "already there" — so it would otherwise stay see-through forever.
      for (const m of entry.mats) {
        const u = m.userData;
        if (u.o0 === undefined) continue;
        m.opacity = u.o0;
        m.depthWrite = u.d0;
        if (m.transparent !== u.t0) { m.transparent = u.t0; m.needsUpdate = true; }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  // the live entry, so a structure's own ticks can fade with it
  return entryRef;
}

// Render a Merger's output as meshes, one per material key.
export function Merged({ geos, mats }) {
  return Object.keys(geos).map((k) => (
    mats[k] ? <mesh key={k} geometry={geos[k]} material={mats[k]} /> : null
  ));
}

function disposeAll(geos, mats) {
  Object.values(geos || {}).forEach((g) => g.dispose());
  Object.values(mats || {}).forEach((m) => m.dispose && m.dispose());
}

// A weathered paint colour for a wing: its brand colour, knocked back
// toward the pier's dusk so twenty-three buildings read as one town
// rather than a paint chart. A closed shop is greyer again.
// Weathered roof tones — salt-faded red, verdigris, slate, tar, sea-blue.
// Bright on purpose: the shingle map is mid-grey and multiplies these down.
const ROOF_TINTS = ["#b27566", "#6f9c94", "#9a93ad", "#a88a64", "#7b8fb3", "#9c6f86"];

function paintFor(color, soon) {
  const c = new THREE.Color(color);
  c.lerp(new THREE.Color("#4a3e46"), soon ? 0.62 : 0.42);
  return c;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE SHOPFRONT — one wing, one building, the door is the portal
// ────────────────────────────────────────────────────────────────────
// Built in LOCAL space: frontage centred on x=0, the facade at z=0 facing
// +z, the building running back to z=-D. The group then stands it on the
// edge and turns it to face the boardwalk. A western false front carries
// the name above the awning — the look of a seaside boardwalk street.
//
// The north side of the pier is two storeys; the south side is one,
// because the default camera stands south of the player and a tall
// south-side frontage would be between it and you all day.
// ════════════════════════════════════════════════════════════════════
export function Shopfront({ portal }) {
  const { store } = portal;
  const soon = portal.status === "coming_soon";

  const built = useMemo(() => {
    const R = mulberry32(hashId(portal.id));
    const W = store.w - 0.12;
    // ⚠ THE SOUTH ROW IS SHALLOWER. From the street camera its roof is most of
    // the frame, and a 3-deep slab of it filled the bottom of every south square.
    const D = store.face === "N" && !store.tall ? 2.4 : 3.0;
    // ⚠ SOUTH STAYS LOW, WHATEVER THE WIDTH. `|| W >= 5.5` made the Casino
    // a two-storey block on the south row, and from the street camera its
    // back wall was a flat band across the bottom of the frame (PRIME L59).
    const tall = store.tall ?? store.face === "S";
    const H1 = 2.05;                         // ground floor
    const BAND = 0.72;                       // the name band
    const H2 = tall ? 1.35 : 0;              // upper floor
    const HT = H1 + BAND + H2;               // eave height
    // ⚠ The south row is LOW AND FLAT unless the plan says otherwise: the
    // default camera stands south of the player, and a gable plus false
    // front on that side hid the boardwalk in front of every south shop.
    const roof = store.roof || (!tall ? (store.face === "N" ? "lean" : "flat") : (R() < 0.3 ? "flat" : "gable"));
    const rise = roof === "gable" ? 0.8 + R() * 0.5 : 0;

    const m = new Merger();
    // ── the shell ──
    m.box("wall", W, HT, D, [0, HT / 2, -D / 2]);
    m.box("timber", W + 0.14, 0.14, D + 0.1, [0, 0.07, -D / 2]);         // sill plate
    for (const sx of [-1, 1]) {
      m.box("trim", 0.13, HT + 0.02, 0.13, [sx * (W / 2 - 0.02), HT / 2, 0.02]);
      m.box("trim", 0.13, HT, 0.13, [sx * (W / 2 - 0.02), HT / 2, -D + 0.04]);
    }
    m.box("trim", W + 0.1, 0.12, 0.16, [0, H1, 0.04]);                   // floor band
    if (tall) m.box("trim", W + 0.1, 0.1, 0.14, [0, H1 + BAND, 0.03]);

    // ── the false front, rising past the eave ──
    const FF = roof === "gable" ? rise + 0.25 : roof === "lean" ? 1.2 : (tall ? 0.45 : 0.3);
    m.box("wall", W, FF, 0.16, [0, HT + FF / 2, -0.08]);
    m.box("trim", W + 0.22, 0.12, 0.26, [0, HT + FF + 0.06, -0.06]);     // cornice
    if (R() < 0.6) {
      const pw = Math.min(W * 0.42, 1.8);
      m.box("wall", pw, 0.28, 0.16, [0, HT + FF + 0.26, -0.08]);
      m.box("trim", pw + 0.14, 0.08, 0.22, [0, HT + FF + 0.44, -0.07]);
    }

    // ── the roof behind it ──
    let attic = null;
    if (roof === "gable") {
      // an attic prism (clapboard) with two shingle slabs on it
      const sx = D / 1.732, sz = rise / 1.5;
      m.add("wall", new THREE.CylinderGeometry(1, 1, W, 3), [0, HT + 0.5 * sz, -D / 2],
        [-Math.PI / 2, 0, Math.PI / 2], [sx, 1, sz]);
      const a = Math.atan2(rise, D / 2);
      const L = Math.hypot(rise, D / 2) + 0.28;
      m.box("roof", W + 0.3, 0.08, L, [0, HT + rise / 2 + 0.05, -D / 4 + 0.02], [-a, 0, 0]);
      m.box("roof", W + 0.3, 0.08, L, [0, HT + rise / 2 + 0.05, -3 * D / 4 - 0.02], [a, 0, 0]);
      m.box("trim", W + 0.32, 0.08, 0.1, [0, HT + rise + 0.07, -D / 2]);  // ridge
      if (R() < 0.7) m.box("stone", 0.34, 0.9, 0.34, [(R() - 0.5) * W * 0.6, HT + rise * 0.6 + 0.3, -D * 0.72]);
    } else if (roof === "lean") {
      // ⭐ THE SOUTH ROW WEARS A LEAN-TO, falling toward the sea — which is
      // toward the street camera. A flat tar roof was a grey slab across the
      // bottom of every south square; a lit shingle slope with an eave reads
      // as a roof at a glance (visual review, 2026-09-14).
      const hi = 0.62, a = Math.atan2(hi, D), L = Math.hypot(hi, D) + 0.25;
      m.box("roof", W + 0.24, 0.08, L, [0, HT + hi / 2 + 0.02, -D / 2 - 0.17], [-a, 0, 0]);
      m.box("trim", W + 0.26, 0.14, 0.07, [0, HT - 0.07, -D - 0.28]);                  // eave board
      // the attic wedge under the slope — its own key: an extrusion is not
      // indexed, and the Merger only merges indexed kit primitives
      const tri = new THREE.Shape();
      tri.moveTo(0, 0); tri.lineTo(-D, 0); tri.lineTo(0, hi); tri.closePath();
      attic = new THREE.ExtrudeGeometry(tri, { depth: W, bevelEnabled: false }).rotateY(-Math.PI / 2).translate(W / 2, HT, 0);
      // the name, on the back of the false front, facing the camera
      const bw = Math.min(W - 0.4, 4.6), bh = Math.min(0.55, bw * 0.19);
      m.box("trim", bw + 0.16, bh + 0.16, 0.08, [0, HT + 0.9, -0.21]);
      m.box("stone", 0.36, 1.2, 0.36, [(R() - 0.5) * (W - 1.2), HT + 0.6, -D * 0.55]);
    } else {
      // ⚠ A flat roof is TAR AND GRAVEL behind a parapet, with the clutter
      // a real one carries — seen from the street camera it is most of the
      // building, and a bare slab of shingles read as a tile floor.
      m.box("tar", W + 0.1, 0.1, D, [0, HT + 0.05, -D / 2]);
      m.box("trim", 0.12, 0.3, D, [-W / 2 + 0.06, HT + 0.2, -D / 2]);
      m.box("trim", 0.12, 0.3, D, [W / 2 - 0.06, HT + 0.2, -D / 2]);
      m.box("trim", W, 0.3, 0.12, [0, HT + 0.2, -D + 0.06]);
      // ⚠ A LOW SHOP SHOWS THE STREET CAMERA ITS ROOF, so its name goes up on
      // a rooftop board facing that way — otherwise the south row was a run of
      // anonymous tar roofs and the Casino had no frontage in any view.
      if (!tall && store.face === "N") {
        const bw = Math.min(W - 0.4, 4.6), bh = Math.min(0.55, bw * 0.19);
        m.box("trim", bw + 0.16, bh + 0.16, 0.08, [0, HT + 0.72, -D + 0.14]);
        for (const sx of [-0.35, 0.35]) m.box("iron", 0.05, 0.5, 0.05, [sx * bw, HT + 0.4, -D + 0.2]);
      }
      // a low shop on a square's SIDE shows the street camera its south
      // flank — local -x for an east-facing door, +x for a west-facing one
      if (!tall && (store.face === "E" || store.face === "W")) {
        const sx = store.face === "E" ? -1 : 1, bw = Math.min(D - 0.5, 2.6);
        m.box("trim", 0.08, bw * 0.19 + 0.16, bw + 0.16, [sx * (W / 2 - 0.14), HT + 0.72, -D / 2]);
      }
      // a chimney stack, a water barrel on a stand, a vent or two — seeded
      m.box("stone", 0.38, 0.8, 0.38, [(R() - 0.5) * (W - 1), HT + 0.5, -D * (0.55 + R() * 0.25)]);
      if (W > 3.2 || R() < 0.5) {
        const bx = (R() < 0.5 ? -1 : 1) * (W / 2 - 0.55), bz = -D * 0.3;
        for (const [dx, dz] of [[-0.2, -0.2], [0.2, -0.2], [-0.2, 0.2], [0.2, 0.2]]) m.box("iron", 0.04, 0.4, 0.04, [bx + dx, HT + 0.3, bz + dz]);
        m.cyl("timber", 0.3, 0.3, 0.55, 12, [bx, HT + 0.78, bz]);
        m.cyl("iron", 0.31, 0.31, 0.04, 12, [bx, HT + 0.62, bz]);
        m.cyl("iron", 0.31, 0.31, 0.04, 12, [bx, HT + 0.94, bz]);
      }
      for (let v = 0; v < 2; v++) {
        const vx = (R() - 0.5) * (W - 0.8), vz = -D * (0.2 + R() * 0.6);
        m.cyl("iron", 0.06, 0.06, 0.34, 8, [vx, HT + 0.27, vz]);
        m.cyl("iron", 0.11, 0.06, 0.08, 8, [vx, HT + 0.46, vz]);
      }
    }

    // ── the door ──
    const DW = W < 3 ? 0.9 : 1.05, DH = 1.72;
    m.box("dark", DW, DH, 0.1, [0, DH / 2 + 0.14, -0.04]);
    // ⚠ A DEEP FRAME, so the door reads as an opening you walk into and not
    // as a disc stuck to the wall: the jambs and head stand 0.36 proud.
    for (const sx of [-1, 1]) m.box("trim", 0.16, DH + 0.14, 0.36, [sx * (DW / 2 + 0.08), DH / 2 + 0.14, 0.17]);
    m.box("trim", DW + 0.46, 0.18, 0.4, [0, DH + 0.24, 0.18]);             // lintel
    m.box("trim", DW + 0.62, 0.08, 0.5, [0, DH + 0.37, 0.22]);             // hood
    m.box("timber", DW + 0.6, 0.1, 0.4, [0, 0.05, 0.2]);                   // step

    // ── the ground-floor windows ──
    const side = (W - DW - 0.3) / 2;
    const wins = [];
    if (side > 0.55) {
      const ww = Math.min(1.5, side - 0.3), wh = 0.95, wy = 1.12;
      for (const sx of [-1, 1]) {
        const cx = sx * (DW / 2 + 0.15 + side / 2);
        wins.push([cx, wy, ww, wh]);
        m.box("trim", ww + 0.16, wh + 0.16, 0.07, [cx, wy, 0.01]);
        m.box("trim", ww + 0.3, 0.07, 0.18, [cx, wy - wh / 2 - 0.1, 0.06]);  // sill
        m.box("timber", ww, 0.4, 0.06, [cx, wy - wh / 2 - 0.34, 0.02]);      // stall board
      }
    }
    // ── the upper windows ──
    const upper = [];
    if (tall) {
      const n = W >= 5 ? 3 : 2;
      const uw = Math.min(0.7, (W - 0.6) / n - 0.35), uh = 0.8, uy = H1 + BAND + H2 / 2 + 0.02;
      for (let i = 0; i < n; i++) {
        const cx = -W / 2 + (W / n) * (i + 0.5);
        upper.push([cx, uy, uw, uh]);
        m.box("trim", uw + 0.14, uh + 0.14, 0.07, [cx, uy, 0.01]);
        m.box("trim", uw + 0.22, 0.06, 0.14, [cx, uy - uh / 2 - 0.07, 0.05]);
        if (!soon && R() < 0.5) {               // shutters, on some
          m.box("wall", uw * 0.5, uh + 0.05, 0.05, [cx - uw * 0.78, uy, 0.04]);
          m.box("wall", uw * 0.5, uh + 0.05, 0.05, [cx + uw * 0.78, uy, 0.04]);
        }
      }
    }

    // ── the name board's backing, the glyph bracket ──
    const signW = Math.min(W - 0.3, 5.2), signH = Math.min(0.58, signW * 0.19);
    m.box("trim", signW + 0.14, signH + 0.14, 0.08, [0, H1 + BAND / 2 + 0.02, 0.07]);
    // ⚠ THE GLYPH HANGS FROM SOMETHING YOU CAN SEE. A hair-thin arm and no
    // chains left the plate floating beside the door from the street camera —
    // the exact "floating pictures" complaint (visual review, 2026-09-14).
    const bx = W / 2 - 0.3;
    m.box("iron", 0.08, 0.08, 0.8, [bx, H1 - 0.1, 0.4]);                          // arm
    m.box("iron", 0.07, 0.36, 0.07, [bx, H1 - 0.2, 0.04]);                        // wall plate
    m.box("iron", 0.045, 0.045, 0.5, [bx, H1 - 0.3, 0.22], [-0.62, 0, 0]);        // brace
    for (const dz of [-0.19, 0.19]) m.box("iron", 0.025, 0.16, 0.025, [bx, H1 - 0.2, 0.5 + dz]);   // chains
    m.box("timber", 0.05, 0.54, 0.54, [bx, H1 - 0.47, 0.5]);                      // the board it is painted on

    // ⭐ A SIDE-STREET SHOP SHOWS THE BOARDWALK ITS FLANK, so the flank gets
    // windows, and a two-storey one a blade sign (visual review, 2026-09-14):
    // from the spawn the first thing you saw was a windowless wall, and
    // KINDRED or HOOKED read at 60° off their own fronts.
    const ew = store.face === "E" || store.face === "W";
    const fs = store.face === "E" ? -1 : 1;           // the flank facing south, toward the camera
    const flank = [];
    if (ew) {
      const rowsY = tall ? [1.12, H1 + BAND + H2 / 2 + 0.02] : [1.12];
      for (const wy of rowsY) for (const wz of [-0.8, -D + 0.8]) {
        flank.push([fs * (W / 2 + 0.04), wy, wz]);
        m.box("trim", 0.07, 0.86, 0.72, [fs * W / 2, wy, wz]);
        m.box("trim", 0.16, 0.06, 0.84, [fs * (W / 2 + 0.05), wy - 0.45, wz]);
      }
    }
    let blade = null;
    if (ew && tall) {
      const len = 2.0, w = len - 0.12, h = w * 0.19;
      blade = { x: fs * (W / 2 - 0.35), y: H1 + BAND + H2 * 0.5, z: 0.25 + len / 2, w, h };
      m.box("timber", 0.06, h + 0.12, len, [blade.x, blade.y, blade.z]);
      m.box("iron", 0.07, 0.07, len + 0.25, [blade.x, blade.y + (h + 0.12) / 2 + 0.035, blade.z - 0.12]);
    }

    // ── the door lanterns ──
    for (const sx of [-1, 1]) {
      m.box("iron", 0.06, 0.06, 0.2, [sx * (DW / 2 + 0.36), 1.62, 0.1]);
      m.box("iron", 0.2, 0.05, 0.2, [sx * (DW / 2 + 0.36), 1.62 + 0.18, 0.2]);
    }

    const geos = m.build();
    ["wall", "roof", "timber", "stone"].forEach((k) => geos[k] && boxUV(geos[k], 0.85));
    if (geos.tar) boxUV(geos.tar, 2.5);
    if (attic) geos.attic = boxUV(attic, 0.85);

    return { W, D, H1, HT, BAND, DW, DH, wins, upper, signW, signH, bx, tall, geos, roof, flank, blade, fs };
  }, [portal.id, store.w, store.face, store.roof, store.tall, soon]);

  const mats = useMemo(() => {
    const paint = paintFor(portal.color, soon);
    const glass = new THREE.Color(soon ? "#ffb070" : "#ffd79a").lerp(new THREE.Color(portal.color), 0.18);
    const wall = new THREE.MeshStandardMaterial({ map: clapTex(), color: paint, roughness: 0.92 });
    return {
      wall,
      attic: wall,          // the lean-to's wedge: same paint, its own geometry
      // ⚠ The shingle map is neutral grey, so the tint IS the roof's value.
      // A dark tint on a dark map made every roof a black slab — and seen
      // from a camera above the street, roofs are most of the frame.
      // Weathered seaside tones, seeded per wing, never pure brand colour.
      roof: new THREE.MeshStandardMaterial({
        map: shingleTex(), roughness: 0.95,
        color: ROOF_TINTS[hashId(portal.label) % ROOF_TINTS.length],
      }),
      // ⚠ A closed shop's trim is WEATHERED, not black. At #2a2320 every
      // edge, cornice and parapet of 21 of 23 buildings went near-black, and
      // from the street camera the south row read as dark shipping crates.
      trim: new THREE.MeshStandardMaterial({ color: soon ? "#9a8e7c" : "#d8c9a8", roughness: 0.8 }),
      // tar and gravel, lit — at #4a4450 on a dark map it was a black slab
      tar: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#8a8094", roughness: 1 }),
      timber: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#8a6e50", roughness: 1 }),
      stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#8a8090", roughness: 1 }),
      iron: new THREE.MeshStandardMaterial({ color: "#1d1924", roughness: 0.55, metalness: 0.6 }),
      dark: new THREE.MeshBasicMaterial({ color: DOOR_DARK }),
      glass: new THREE.MeshStandardMaterial({
        color: "#0c0810", emissive: glass, emissiveMap: windowTex(soon),
        emissiveIntensity: soon ? 1.05 : 1.6, roughness: 0.25, toneMapped: false,
      }),
      boards: new THREE.MeshStandardMaterial({ map: boardsTex(), transparent: true, alphaTest: 0.4, roughness: 1 }),
      door: new THREE.MeshStandardMaterial({ map: doorTex(), roughness: 0.8, emissive: "#1a0e06", emissiveIntensity: 0.3 }),
      awning: new THREE.MeshStandardMaterial({
        map: awningTex(portal.color), transparent: true, alphaTest: 0.35,
        side: THREE.DoubleSide, roughness: 0.9,
        color: soon ? "#8a7f78" : "#ffffff",
      }),
      sign: new THREE.MeshBasicMaterial({ map: signTex(portal.label, portal.color, soon), toneMapped: false }),
      glyph: new THREE.MeshBasicMaterial({ map: glyphTex(portal.icon, soon ? "#8f8577" : portal.color), side: THREE.DoubleSide, toneMapped: false }),
      neon: new THREE.MeshBasicMaterial({
        map: neonTex(soon ? "SOON" : "OPEN", soon ? "#f87171" : portal.color),
        transparent: true, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
      }),
      rift: new THREE.MeshBasicMaterial({
        map: riftTex(portal.color), transparent: true, depthWrite: false, toneMapped: false,
        blending: THREE.AdditiveBlending, opacity: soon ? 0.35 : 0.95,
      }),
      lamp: new THREE.MeshBasicMaterial({ color: "#ffd9a0", toneMapped: false }),
      spill: new THREE.MeshBasicMaterial({
        map: poolTex(), color: portal.color, transparent: true, depthWrite: false,
        blending: THREE.AdditiveBlending, toneMapped: false, opacity: soon ? 0.22 : 0.6,
      }),
    };
  }, [portal.color, portal.label, portal.icon, soon]);

  useEffect(() => () => disposeAll(built.geos, mats), [built, mats]);

  // the rift turns and the neon hums — one tick, registered once
  const ref = React.useRef();
  useTick((t) => {
    const map = mats.rift.map;
    map.center.set(0.5, 0.5);
    map.rotation = t * (soon ? 0.12 : 0.45);
    const hum = soon
      ? (Math.sin(t * 0.7 + 1) > 0.93 ? 0.25 : 0.7)
      : 0.85 + 0.15 * Math.sin(t * 2.3) * (Math.sin(t * 13) > 0.97 ? -3 : 1);
    // additive layers are animated here, so they take the fade here
    const k = occ.current ? occ.current.k : 1;
    mats.neon.opacity = Math.max(0.1, Math.min(1, hum)) * k;
    mats.rift.opacity = (soon ? 0.35 : 0.95) * k;
    mats.spill.opacity = (soon ? 0.22 : 0.6) * k;
  }, [mats, soon]);

  // everything that can stand between the lens and the player fades
  const mass = useMemo(() => new THREE.Box3(
    new THREE.Vector3(-built.W / 2, 0, -built.D),
    // ⚠ the box ENDS AT THE WALL. At +0.12 a player standing flush against
    // a south-row front was 'inside' it, the driver skipped the building,
    // and it hid them with no fade. (Second adversarial review.)
    new THREE.Vector3(built.W / 2, built.HT + 1.4, -0.02),
  ), [built]);
  // ⚠ The lanterns and the hanging glyph fade WITH the building. Left out,
  // a ghosted shop left two lit cubes and a sign floating in mid-air.
  const occ = useOccluder(ref, mass, [mats.wall, mats.roof, mats.tar, mats.trim, mats.timber, mats.stone, mats.glass, mats.sign, mats.awning, mats.iron, mats.dark, mats.boards, mats.door, mats.lamp, mats.glyph], [built, mats, mass]);

  const { W, D, HT, H1, BAND, DW, DH, wins, upper, signW, signH, bx, tall, geos, roof, flank, blade, fs } = built;
  // S faces +z, N faces −z, E faces +x, W faces −x; the building runs back behind its door
  const rotY = { S: 0, N: Math.PI, E: Math.PI / 2, W: -Math.PI / 2 }[store.face] ?? 0;
  const onNS = store.face === "S" || store.face === "N";
  const placeAt = onNS ? [store.x + store.w / 2, 0, store.y] : [store.x, 0, store.y + store.w / 2];
  const neonWin = wins[1] || wins[0];

  return (
    <group ref={ref} position={placeAt} rotation={[0, rotY, 0]}>
      <Merged geos={geos} mats={mats} />

      {/* the name */}
      <mesh position={[0, H1 + BAND / 2 + 0.02, 0.116]} material={mats.sign}>
        <planeGeometry args={[signW, signH]} />
      </mesh>

      {/* the door: through it, the wing's own world */}
      <mesh position={[0, DH / 2 + 0.14, 0.02]} material={mats.rift}>
        <planeGeometry args={[DW - 0.04, DH - 0.04]} />
      </mesh>
      {/* ⚠ A SHOP GETTING READY TO OPEN HAS A SHUT DOOR, NOT AN X OF PLANKS.
          Twenty-one boarded doors read as a condemned street. */}
      {soon && (
        <mesh position={[0, DH / 2 + 0.14, 0.05]} material={mats.door}>
          <planeGeometry args={[DW - 0.02, DH - 0.02]} />
        </mesh>
      )}
      {!tall && store.face === "N" && (
        <mesh position={roof === "lean" ? [0, HT + 0.9, -0.26] : [0, HT + 0.72, -D + 0.09]} rotation={[0, Math.PI, 0]} material={mats.sign}>
          <planeGeometry args={[Math.min(W - 0.4, 4.6), Math.min(0.55, Math.min(W - 0.4, 4.6) * 0.19)]} />
        </mesh>
      )}

      {!tall && (store.face === "E" || store.face === "W") && (
        <mesh position={[(store.face === "E" ? -1 : 1) * (W / 2 - 0.09), HT + 0.72, -D / 2]}
              rotation={[0, store.face === "E" ? -Math.PI / 2 : Math.PI / 2, 0]} material={mats.sign}>
          <planeGeometry args={[Math.min(D - 0.5, 2.6), Math.min(D - 0.5, 2.6) * 0.19]} />
        </mesh>
      )}

      {/* windows */}
      {[...wins, ...upper].map(([cx, cy, ww, wh], i) => (
        <group key={i}>
          <mesh position={[cx, cy, 0.05]} material={mats.glass}>
            <planeGeometry args={[ww, wh]} />
          </mesh>
        </group>
      ))}
      {neonWin && (
        <mesh position={[neonWin[0], neonWin[1] + 0.12, 0.09]} material={mats.neon}>
          <planeGeometry args={[Math.min(0.8, neonWin[2] * 0.85), Math.min(0.8, neonWin[2] * 0.85) * 0.4]} />
        </mesh>
      )}

      {/* the awning over the shopfront */}
      <mesh position={[0, H1 - 0.2, 0.36]} rotation={[-1.08, 0, 0]} material={mats.awning}>
        <planeGeometry args={[W * 0.94, 0.86]} />
      </mesh>

      {/* the hanging glyph, edge-on to the street so passers-by read it */}
      {[-1, 1].map((s) => (
        <mesh key={`g${s}`} position={[bx + s * 0.03, H1 - 0.47, 0.5]} rotation={[0, s * Math.PI / 2, 0]} material={mats.glyph}>
          <planeGeometry args={[0.44, 0.44]} />
        </mesh>
      ))}

      {/* the flank's windows, and the blade sign over a side street */}
      {flank.map(([fx, fy, fz], i) => (
        <mesh key={`f${i}`} position={[fx, fy, fz]} rotation={[0, fs * Math.PI / 2, 0]} material={mats.glass}>
          <planeGeometry args={[0.58, 0.72]} />
        </mesh>
      ))}
      {blade && [-1, 1].map((s) => (
        <mesh key={`b${s}`} position={[blade.x + s * 0.035, blade.y, blade.z]} rotation={[0, s * Math.PI / 2, 0]} material={mats.sign}>
          <planeGeometry args={[blade.w, blade.h]} />
        </mesh>
      ))}

      {/* door lanterns */}
      {[-1, 1].map((sx) => (
        <mesh key={sx} position={[sx * (DW / 2 + 0.36), 1.62 + 0.04, 0.2]} material={mats.lamp}>
          <boxGeometry args={[0.13, 0.2, 0.13]} />
        </mesh>
      ))}

      {/* the doorway's light on the planks */}
      <mesh position={[0, 0.02, 0.95]} rotation={[-Math.PI / 2, 0, 0]} material={mats.spill}>
        <planeGeometry args={[2.6, 1.9]} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE DRIVER — one useFrame for every animated structure, and the fader
// ────────────────────────────────────────────────────────────────────
// A real boardwalk has buildings on the camera's side of you too. When a
// structure's mass stands between the lens and the player it thins to a
// ghost and comes back when it is out of the way — the way every
// third-person game with a town in it has solved this.
// ⚠ Materials flip to transparent ONLY while fading, and back, with a
// needsUpdate on each flip. Leaving a town permanently transparent costs
// correct depth sorting everywhere for a state it is in rarely.
// ════════════════════════════════════════════════════════════════════
const _ray = new THREE.Ray();
const _hit = new THREE.Vector3();
// five rays' scratch — allocated once, never per frame
const _T = [0, 1, 2, 3, 4].map(() => new THREE.Vector3());
const _D = [0, 1, 2, 3, 4].map(() => new THREE.Vector3());
const _L = [0, 0, 0, 0, 0];
// ⚠ 0, not 0.16 and not 0.06. A building is several faces deep from above
// — roof, front, side, false front — and at 16% they stacked into a dark
// smoked box; at 6% the Hall of Deeds, which sits right under the camera
// whenever you walk the spine past the Crest Well, still laid a lilac haze
// of magnified stonework across half the frame. A clean cutaway reads
// better than any smoke: the structure steps aside, then comes back.
const GHOST = 0;

export function BuildDriver({ posRef }) {
  useFrame(({ clock, camera }, dt) => {
    const t = clock.elapsedTime;
    BUILD_TICKS.forEach((fn) => { try { fn(t, dt); } catch { /* one bad tick never stops the rest */ } });

    // ⚠ NOT ONE RAY, FIVE. A single ray to the player's chest cleared the
    // south-side rooftops by a hand's width and left them filling the
    // bottom third of the frame — the player was visible, the boardwalk
    // around them was not. So the fader also asks about the planks just
    // in front of the player and to either side: if a building hides the
    // ground you are about to walk on, it is in the way.
    const p = posRef.current;
    const fx0 = p.x - camera.position.x, fz0 = p.y - camera.position.z;
    const fl = Math.hypot(fx0, fz0) || 1;
    const fx = fx0 / fl, fz = fz0 / fl;
    _T[0].set(p.x, 0.9, p.y);
    _T[1].set(p.x - fx * 2.4, 0.1, p.y - fz * 2.4);
    _T[2].set(p.x - fx * 1.2 - fz * 2.0, 0.3, p.y - fz * 1.2 + fx * 2.0);
    _T[3].set(p.x - fx * 1.2 + fz * 2.0, 0.3, p.y - fz * 1.2 - fx * 2.0);
    _T[4].set(p.x + fx * 1.5, 0.3, p.y + fz * 1.5);
    for (let i = 0; i < 5; i++) {
      _D[i].subVectors(_T[i], camera.position);
      _L[i] = _D[i].length();
      _D[i].divideScalar(_L[i] || 1);
    }
    // ~0.3 s to step aside: long enough not to pop, short enough that a
    // passing structure never lingers as a sheet across the lens
    const k = 1 - Math.pow(0.00002, Math.min(dt || 0.016, 0.05));

    OCCLUDERS.forEach((o) => {
      // (no whole-structure skip for a player inside the box — the per-probe
      // skip below already ignores any probe point that lies within it)
      let blocks = o.box.containsPoint(camera.position);
      for (let i = 0; i < 5 && !blocks; i++) {
        // ⚠ A PROBE POINT INSIDE THIS STRUCTURE IS NOT HIDDEN BY IT — it IS
        // it. Without this skip the shop you walked up to ghosted at its own
        // doorstep, because the probe past the player landed inside its
        // walls. Found by adversarial review 2026-09-13.
        if (o.box.containsPoint(_T[i])) continue;
        _ray.set(camera.position, _D[i]);
        if (_ray.intersectBox(o.box, _hit) && camera.position.distanceTo(_hit) < _L[i] - 0.2) blocks = true;
      }
      const want = blocks ? GHOST : 1;
      if (o.k === want) return;
      o.k += (want - o.k) * k;
      if (Math.abs(o.k - want) < 0.01) o.k = want;
      const fading = o.k < 1;
      for (const m of o.mats) {
        const u = m.userData;
        if (u.o0 === undefined) { u.o0 = m.opacity; u.t0 = m.transparent; u.d0 = m.depthWrite; }
        const tr = fading || u.t0;
        if (m.transparent !== tr) { m.transparent = tr; m.needsUpdate = true; }
        m.opacity = u.o0 * o.k;
        m.depthWrite = fading ? false : u.d0;
      }
    });
  });
  return null;
}
